import { useCallback, useEffect, useState } from 'react'
import { Loader2, UserPlus, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErpTable, MetricCard, Td, Th } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, LoadingRows, Note } from '@/shared/ui/states'
import { Modal } from '@/shared/ui/Modal'
import { fmtDate } from '@/shared/lib/format'
import { EntityPicker } from '@/shared/pickers/EntityPicker'
import {
  closeMembership, createMembership, listMemberships, transferMembership,
  type Membership,
} from './api'
import { MembershipStatusChip } from './vocab'

/**
 * The membership register.
 *
 * ## One member, one party — and the history survives the change
 *
 * The database enforces at most one ACTIVE membership per voter with a PARTIAL unique index,
 * not a plain one. That distinction is the feature: a defection closes the old row and opens
 * a new one, and last cycle's roll stays reconstructable. Joining a second party outright is
 * refused with a message naming the first, so the operator sees a sentence rather than a
 * constraint violation.
 *
 * ## Everything is keyed by national ID
 *
 * That is what an official has on the signup form, and what the member will later type to
 * vote. The plaintext never becomes searchable — resolution happens server-side through the
 * register's blind index.
 */
export function MembersPage() {
  const [partyId, setPartyId] = useState<string | null>(null)
  const [status, setStatus] = useState('ACTIVE')
  const [rows, setRows] = useState<Membership[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [moving, setMoving] = useState(false)

  const load = useCallback(async () => {
    try {
      setError(null)
      setRows(await listMemberships({
        partyId, status: status === 'ALL' ? null : status, size: 300,
      }))
    } catch (err) {
      setError(explain(err))
      setRows([])
    }
  }, [partyId, status])

  useEffect(() => { void load() }, [load])

  const active = rows?.filter((r) => r.statusCode === 'ACTIVE').length ?? 0

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-erp-base font-semibold">Members</h2>
          <p className="text-erp-xs text-muted-foreground mt-0.5">
            Who belongs to which party, linked to the voter register by ID number.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setMoving(true)}
            className="erp-btn h-9 px-3 text-erp-sm inline-flex items-center gap-2">
            <Users className="h-3.5 w-3.5" /> Transfer
          </button>
          <button type="button" onClick={() => setAdding(true)}
            className="erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2">
            <UserPlus className="h-3.5 w-3.5" /> Add a member
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">Party</span>
          <EntityPicker source="party" value={partyId} onChange={setPartyId}
            placeholder="Any party" />
        </label>
        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            className="erp-input h-9 text-erp-sm">
            {['ACTIVE', 'LEFT', 'SUSPENDED', 'EXPELLED', 'ALL'].map((s) => (
              <option key={s} value={s}>{s === 'ALL' ? 'All' : s.toLowerCase()}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <MetricCard label="Shown" value={String(rows?.length ?? 0)} />
        <MetricCard label="Active" value={String(active)} />
      </div>

      <ErrorStrip message={error} className="rounded-md border" />

      {rows !== null && rows.length === 0 && !error && (
        <EmptyState
          title="No members yet"
          hint="A member is a voter on the register who has joined a party. Add them by ID
                number, or import a list — nothing else in this module works without a roll." />
      )}

      {(rows === null || rows.length > 0) && (
        <ErpTable head={(
          <>
            <Th>Member</Th>
            <Th>Party</Th>
            <Th>Number</Th>
            <Th>Status</Th>
            <Th>Joined</Th>
            <Th>Left</Th>
            <Th />
          </>
        )}>
          {rows === null ? <LoadingRows colSpan={7} />
            : rows.map((m) => (
              <tr key={m.id}>
                <Td>{m.voterName ?? '—'}</Td>
                <Td>{m.partyName ?? '—'}</Td>
                <Td className="text-erp-xs text-muted-foreground">{m.memberNumber ?? '—'}</Td>
                <Td><MembershipStatusChip code={m.statusCode} /></Td>
                <Td className="text-erp-xs">{fmtDate(m.joinedOn)}</Td>
                <Td className="text-erp-xs text-muted-foreground">
                  {m.leftOn ? `${fmtDate(m.leftOn)}${m.leftReasonCode ? ` · ${m.leftReasonCode.toLowerCase()}` : ''}` : '—'}
                </Td>
                <Td>
                  {m.statusCode === 'ACTIVE' && (
                    <CloseButton membership={m} onDone={load} />
                  )}
                </Td>
              </tr>
            ))}
        </ErpTable>
      )}

      <AddMemberModal open={adding} onClose={() => setAdding(false)} onDone={load} />
      <TransferModal open={moving} onClose={() => setMoving(false)} onDone={load} />
    </div>
  )
}

// ── add ──────────────────────────────────────────────────────────────────────────────────

function AddMemberModal({ open, onClose, onDone }: {
  open: boolean; onClose: () => void; onDone: () => void | Promise<void>
}) {
  const [nationalId, setNationalId] = useState('')
  const [partyId, setPartyId] = useState<string | null>(null)
  const [memberNumber, setMemberNumber] = useState('')
  const [verify, setVerify] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) return
    setNationalId(''); setPartyId(null); setMemberNumber(''); setVerify(true)
    setError(null); setBusy(false)
  }, [open])

  const submit = async () => {
    if (!partyId) return
    setBusy(true)
    try {
      setError(null)
      await createMembership({
        nationalId: nationalId.trim(), partyId,
        memberNumber: memberNumber.trim() || null, verifyIdentity: verify,
      })
      await onDone()
      onClose()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Add a member" width="md">
      <div className="space-y-3">
        <ErrorStrip message={error} className="rounded-md border" />
        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">ID number</span>
          <input value={nationalId} onChange={(e) => setNationalId(e.target.value)}
            className="erp-input h-9 w-full text-erp-sm" maxLength={64}
            placeholder="As printed on the document" />
        </label>
        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">Party</span>
          <EntityPicker source="party" value={partyId} onChange={setPartyId} />
        </label>
        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">
            Member number (optional)
          </span>
          <input value={memberNumber} onChange={(e) => setMemberNumber(e.target.value)}
            className="erp-input h-9 w-full text-erp-sm" maxLength={64} />
        </label>
        <label className="inline-flex items-center gap-2 text-erp-sm">
          <input type="checkbox" checked={verify} onChange={(e) => setVerify(e.target.checked)} />
          Check the ID against IPRS
        </label>
        <Note>
          The person must already be on the voter register — a member who is not cannot be
          authenticated later, so admitting them would create a row that can never vote.
        </Note>
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="erp-btn h-9 px-3 text-erp-sm">
            Cancel
          </button>
          <button type="button" disabled={busy || !partyId || !nationalId.trim()}
            onClick={() => void submit()}
            className={cn('erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2',
              (busy || !partyId || !nationalId.trim()) && 'opacity-50 cursor-not-allowed')}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Add
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ── transfer ─────────────────────────────────────────────────────────────────────────────

function TransferModal({ open, onClose, onDone }: {
  open: boolean; onClose: () => void; onDone: () => void | Promise<void>
}) {
  const [nationalId, setNationalId] = useState('')
  const [toPartyId, setToPartyId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) return
    setNationalId(''); setToPartyId(null); setError(null); setBusy(false)
  }, [open])

  const submit = async () => {
    if (!toPartyId) return
    setBusy(true)
    try {
      setError(null)
      await transferMembership({ nationalId: nationalId.trim(), toPartyId })
      await onDone()
      onClose()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Transfer a member" width="md">
      <div className="space-y-3">
        <ErrorStrip message={error} className="rounded-md border" />
        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">ID number</span>
          <input value={nationalId} onChange={(e) => setNationalId(e.target.value)}
            className="erp-input h-9 w-full text-erp-sm" maxLength={64} />
        </label>
        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">New party</span>
          <EntityPicker source="party" value={toPartyId} onChange={setToPartyId} />
        </label>
        <Note>
          This closes the current membership as a defection and opens the new one in the same
          transaction. The old row keeps its dates — a roll frozen before today still contains
          the member as they were.
        </Note>
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="erp-btn h-9 px-3 text-erp-sm">
            Cancel
          </button>
          <button type="button" disabled={busy || !toPartyId || !nationalId.trim()}
            onClick={() => void submit()}
            className={cn('erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2',
              (busy || !toPartyId || !nationalId.trim()) && 'opacity-50 cursor-not-allowed')}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Transfer
          </button>
        </div>
      </div>
    </Modal>
  )
}

function CloseButton({ membership, onDone }: {
  membership: Membership; onDone: () => void | Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const act = async (expelled: boolean) => {
    const reason = window.prompt(expelled
      ? 'Reason for expulsion (recorded permanently):'
      : 'Reason for leaving (recorded permanently):')
    if (!reason) return
    setBusy(true)
    try {
      await closeMembership(membership.id, { expelled, reason })
      await onDone()
    } finally {
      setBusy(false)
    }
  }
  return (
    <span className="inline-flex items-center gap-2">
      {busy && <Loader2 className="h-3 w-3 animate-spin" />}
      <button type="button" onClick={() => void act(false)}
        className="text-erp-xs text-chocolate hover:underline">End</button>
      <button type="button" onClick={() => void act(true)}
        className="text-erp-xs text-red-700 dark:text-red-400 hover:underline">Expel</button>
    </span>
  )
}
